import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { forceCheck } from 'react-lazyload';
import _find from 'lodash/find';
import _debounce from 'lodash/debounce';
import { TagV2 } from '@zola/zola-ui/src/components/Tag/TagV2';
import MatchThemeTag from 'paper/components/Tags/MatchThemeTag';
import { Link } from '@zola/zola-ui/src/components/Link';
import { getCardSuitePath } from '@zola/zola-ui/src/paper/cards/util/getCardUrl';
import {
  isPaperAddOnCardType,
  CARD_TYPE_MAP,
} from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { getSingularTitleByCardType } from '@zola/zola-ui/src/paper/cards/constants/titleSingularByCardType';
import {
  ADD_ONS_COMPATIBILITY_CAPTION,
  ADD_ONS_COMPATIBILITY_CAPTION_MWEB,
} from 'cards/constants/addOnsCompatibilityCaptions';
import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { getActiveThumbnailUrl, getFilteredShape } from 'paper/utils/helpers';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import SampleCheckbox from 'cards/components/common/Samples/Layout/Checkbox';
import ProductColorPicker from 'cards/components/common/ProductColorPicker/ProductColorPicker';
import { FavoriteSuiteTogglePlaceholder } from 'favorites/components/FavoriteSuiteToggle/FavoriteSuiteTogglePlaceholder';
import { LazyCardImage } from 'components/common/ui/CardImage';
import { FoilTag } from 'cards/components/common/Tags/FoilTag';
import { LetterpressTag } from 'cards/components/common/Tags/LetterpressTag';
import { MagicTag } from 'cards/components/common/Tags/MagicTag';
import PhotoPreview from 'cards/components/CardListing/PhotoPreview/Preview';
import { ProductPrice } from 'cards/components/CardDetail/ProductPrice';
import { getMultiSampleState } from 'cards/selectors/samples';
import {
  setMultiSampleSelected as setMultiSampleSelectedAction,
  setMultiSampleEnabled as setMultiSampleEnabledAction,
} from 'cards/actions/samples';
import { trackEvent } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import { trackProductClicked } from '@zola/tracking-contracts/src/tracking';
import { mapCardTypeToBusinessCategory } from '@zola/zola-ui/src/paper/cards/util/trackingHelpers';
import { storeInitialColor, storeInitialShape } from 'cards/util/initialOptions';
import styles from './styles.module.less';
import { ProductType, StyledTagV2 } from './styles';
import { QuickAddToCartCTA } from './QuickAddToCartCTA';

const FavoriteSuiteToggle = dynamic(() => import('favorites/components/FavoriteSuiteToggle'), {
  ssr: false,
  loading: () => <FavoriteSuiteTogglePlaceholder height={20} className={styles.favoriteToggle} />,
});

const INVITATION_ALT_TEXTS = {
  '5d17b42c-508f-480e-948d-30689b9cf460': 'eucalyptus greenery wedding invitation',
  'adf09873-7389-46dc-bd7c-4c1041066e1c': 'brushstroke watercolor wedding invitation',
  '22b13c6e-1a13-4a78-96c8-f5d9d5cde78c': 'floral elegant wedding invitation',
  '7cb2c469-7403-4ea4-b099-b0fc375b851d': 'greenery wedding invitation with string lights',
  '80caad96-3ed7-446a-b0c7-a4f95580efce': 'simple cursive wedding invitation',
  '63122187-ff18-444c-a54e-793a71b3aa49': 'simple black background wedding invitation ',
  'f276de10-0fb8-45f1-9902-5fcbe41fd19d': 'greenery wedding invitation with leafy border',
  'dc4c94dd-ce39-4434-a47e-fc02450c6d8e': 'greenery wedding invitation with flowers',
  '1ac493fa-cb99-4fd7-85b7-a6b992909281': 'black background wedding invitation with flowers',
  'ec57f548-67b8-48ff-8999-43c489de41de': 'watercolor wedding invitation with mountain landscape',
};

function isMultiSampleCheckboxDisabled(suiteUUID, multiSampleState) {
  return (
    !multiSampleState.selected[suiteUUID] &&
    Object.keys(multiSampleState.selected).length >= multiSampleState.count.limit
  );
}

function getActiveColorInfo(
  colors,
  filterColor,
  initialVariationColor,
  filterMultiColor,
  colorRecommendations,
  motifColorRecommendations,
  orderedFilteredColours,
  searchFilters
) {
  const hasMotifColorRecommendations =
    motifColorRecommendations && Object.keys(motifColorRecommendations).length > 0;
  const hasColorRecommendations =
    colorRecommendations && Object.keys(colorRecommendations).length > 0;

  // default activeColorObject is the first object in colors array
  let activeColorObject =
    (hasMotifColorRecommendations && motifColorRecommendations[0]) ||
    (hasColorRecommendations && colorRecommendations[0]) ||
    (colors && colors[0]);

  if (orderedFilteredColours && (hasColorRecommendations || hasMotifColorRecommendations)) {
    let foundColour;
    let foundColorObject;

    // Search through motif colour recommendations first
    if (hasMotifColorRecommendations) {
      foundColour = orderedFilteredColours.find(
        filteredColour => motifColorRecommendations[filteredColour]
      );

      foundColorObject = _find(
        colors,
        data => data.value && data.value === motifColorRecommendations[foundColour]
      );
    }

    // If a colour isn't found in the motif recommendations, then look through the colour recommendations
    if (!foundColour && hasColorRecommendations) {
      foundColour = orderedFilteredColours.find(
        filteredColour => colorRecommendations[filteredColour]
      );

      foundColorObject = _find(
        colors,
        data => data.value && data.value === colorRecommendations[foundColour]
      );
    }

    if (foundColorObject && foundColorObject.value) {
      // check that foundColorObject is truthy and has a .value that is also truthy, if both
      // set activeColorObject to be the foundColorObject
      activeColorObject = foundColorObject;
    }
  } else if (filterMultiColor) {
    // Find the first card color that matches one of the filterMulticolors
    const foundColorObject = _find(
      colors,
      data =>
        data.nrf_color_code_label &&
        filterMultiColor.includes(data.nrf_color_code_label.toUpperCase())
    );

    if (foundColorObject && foundColorObject.value) {
      // check that foundColorObject is truthy and has a .value that is also truthy, if both
      // set activeColorObject to be the foundColorObject
      activeColorObject = foundColorObject;
    }
  } else if (filterColor || initialVariationColor) {
    // if there is a filter color, find the color with nrf_color_code label that matches
    // the filterColor
    const foundColorObject = _find(colors, data =>
      filterColor
        ? data.nrf_color_code_label.toUpperCase() === filterColor.toUpperCase()
        : data.value.toUpperCase() === initialVariationColor.toUpperCase()
    );
    if (foundColorObject && foundColorObject.value) {
      // check that foundColorObject is truthy and has a .value that is also truthy, if both
      // set activeColorObject to be the foundColorObject
      activeColorObject = foundColorObject;
    }
  }

  return {
    activeColor: activeColorObject && activeColorObject.value,
    activeThumbnail: getActiveThumbnailUrl(activeColorObject, searchFilters),
  };
}

const Tag = ({ cardType, isMobile }) => {
  const tagCopy = isMobile
    ? ADD_ONS_COMPATIBILITY_CAPTION_MWEB[cardType]
    : ADD_ONS_COMPATIBILITY_CAPTION[cardType];

  if (!tagCopy) return null;

  return <StyledTagV2>{tagCopy}</StyledTagV2>;
};

const PriceElement = ({ isDigital, card, promotionData }) => (
  <div className={styles.tilePrice}>
    <ProductPrice
      price={card.min_price_cents / 100}
      isFree={isDigital || !card.min_price_cents}
      promotionData={promotionData}
    />
  </div>
);

const CheckBoxElement = ({
  className,
  checkboxDisabled,
  isMultiSampleSelection,
  card,
  activeColor,
  setMultisampleSelected,
}) => (
  <SampleCheckbox
    className={className}
    id={`sample-checked__${card.suiteUUID}`}
    disabled={checkboxDisabled}
    label=""
    checked={isMultiSampleSelection}
    onChange={() => setMultisampleSelected(card, !isMultiSampleSelection, activeColor)}
  />
);

export const ListingDetailBase = ({
  tileIndex,
  card,
  filterColor,
  initialVariationColor,
  filterMultiColor,
  isAdminView,
  lightView,
  collaborator,
  allowMultiSample,
  multiSampleState,
  setMultiSampleEnabled,
  setMultisampleSelected,
  plpOnClickCallback,
  searchFilters = {},
  className,
  isMobile = false,
  bypassLazy = false,
  promotionData,
  allowQuickAddToCart = false,
  cardImageSizes,
  defaultImageWidth,
}) => {
  const isPaperAddOns = isPaperAddOnCardType(card.type);
  const isFavouritesPage = false; // featureFlags.get('paperAddOns') && window.location.pathname === '/favorites/paper';
  const showProductType = isPaperAddOns || isFavouritesPage;

  const { motifs, multiColor, seo_colors } = searchFilters;
  const {
    motif_color_recommendations: motifColorRecommendations,
    color_recommendations: colorRecommendations,
    medium,
    isThemeMatchedCard,
    orientation,
  } = card;
  const isDigital = medium === 'DIGITAL';

  const [preselectedColourListing, setPreselectedColourListing] = useState([]);
  const [activeColorInfo, setActiveColorInfo] = useState(
    getActiveColorInfo(
      card.colors,
      filterColor,
      initialVariationColor,
      filterMultiColor,
      colorRecommendations,
      motifColorRecommendations,
      preselectedColourListing,
      searchFilters
    )
  );

  const photoPreviewTemplate = useMemo(
    () =>
      card.colors.find(color => color.value === activeColorInfo.activeColor)?.photo_preview || {
        card_dimensions: {
          width: card.width,
          height: card.height,
          x: 0,
          y: 0,
        },
      },
    [card, activeColorInfo]
  );

  const [maxColors, setMaxColors] = useState(3);

  useEffect(() => {
    setMaxColors(isDesktop() ? 6 : 3);
  }, []);

  useEffect(() => {
    setPreselectedColourListing([...(motifs || []), ...(seo_colors || []), ...(multiColor || [])]);
  }, [motifs, multiColor, seo_colors]);

  useEffect(() => {
    const newCardInfo = getActiveColorInfo(
      card.colors,
      filterColor,
      initialVariationColor,
      filterMultiColor,
      colorRecommendations,
      motifColorRecommendations,
      preselectedColourListing,
      searchFilters
    );
    setActiveColorInfo(newCardInfo);
  }, [
    card,
    filterColor,
    initialVariationColor,
    filterMultiColor,
    colorRecommendations,
    motifColorRecommendations,
    preselectedColourListing,
  ]);

  useEffect(() => {
    forceCheck();
  }, [tileIndex]);

  function handleSelectColor(colorObject) {
    if (multiSampleState.enabled && multiSampleState.selected[card.suiteUUID]) {
      setMultisampleSelected(card, !!multiSampleState.selected[card.suiteUUID], colorObject.value);
    }

    setActiveColorInfo({
      activeColor: colorObject.value,
      activeThumbnail: getActiveThumbnailUrl(colorObject, searchFilters),
    });
  }

  const handleClick = _debounce((e, pathname) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (allowMultiSample && multiSampleState.enabled) {
      const checkboxDisabled = isMultiSampleCheckboxDisabled(card.suiteUUID, multiSampleState);

      if (!checkboxDisabled) {
        setMultisampleSelected(
          card,
          !multiSampleState.selected[card.suiteUUID],
          activeColorInfo.activeColor
        );
      }
      return;
    }

    if (activeColorInfo.activeColor && !lightView) {
      storeInitialColor(card.suiteUUID, activeColorInfo.activeColor);
    }
    if (!isPaperAddOns) {
      storeInitialShape(getFilteredShape(searchFilters, card.colors?.[0]?.default_variation_shape));
    }

    const { suiteUUID, suiteName, min_price_cents: minPriceCents, type, colors, name } = card;
    const imageUrl = getActiveThumbnailUrl(colors?.[0], searchFilters);
    const trackingType = mapCardTypeToBusinessCategory(type, isDigital);
    const trackingData = {
      business_unit: 'PAPER',
      business_category: trackingType,
      entity_type: 'PAPER',
      card_suite_uuid: suiteUUID,
      sku: colors.find(color => color.value === activeColorInfo.activeColor)
        ?.default_variation_uuid,
      category: trackingType,
      name,
      brand: suiteName,
      price: +(minPriceCents / 100).toFixed(2),
      quantity: 1,
      image_url: imageUrl || '',
      position: tileIndex || 0,
    };
    trackProductClicked(trackingData);

    if (typeof plpOnClickCallback === 'function') {
      plpOnClickCallback();
    }

    window.location = pathname;
  }, 1);

  const pathname = getCardSuitePath(card.type, card.suiteUUID, isAdminView, isDigital);

  const isMultiSampleSelection = !!multiSampleState.selected[card.suiteUUID];

  const checkboxDisabled =
    allowMultiSample &&
    multiSampleState.enabled &&
    isMultiSampleCheckboxDisabled(card.suiteUUID, multiSampleState);

  const multiSampleStateActive = allowMultiSample && multiSampleState.enabled;

  const hasAltText =
    card.type === CARD_TYPE_MAP.invitation &&
    Object.prototype.hasOwnProperty.call(INVITATION_ALT_TEXTS, card.suiteUUID);
  const altText = hasAltText ? INVITATION_ALT_TEXTS[card.suiteUUID] : card.name;

  const enableSamples = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      trackEvent('CTA Clicked', {
        business_unit: BUSINESS_UNIT.INVITATIONS,
        cta_type: 'BUTTON',
        cta: 'PLP_CARD_SAMPLE_HOVER_BUTTON',
      });

      if (!multiSampleState?.enabled) {
        setMultiSampleEnabled(card.type, true);
      }

      setMultisampleSelected(card, true, activeColorInfo.activeColor);
    },
    [
      activeColorInfo.activeColor,
      card,
      multiSampleState?.enabled,
      setMultiSampleEnabled,
      setMultisampleSelected,
    ]
  );

  return (
    <div
      id={card.key}
      className={cx(styles.listingDetail, styles.tileLinkBox, styles.leftAlign, className, {
        [styles.disabledSampleTile]: checkboxDisabled,
      })}
    >
      <Link
        role="link"
        className={styles.link}
        href={pathname}
        data-testid="link"
        onClick={e => handleClick(e, pathname)}
        aria-label={altText}
      >
        <div className={styles.image}>
          {isDigital && (
            <div className={styles.digitalBubble}>
              <div className={styles.digitalBubbleInner}>DIGITAL</div>
            </div>
          )}
          <PhotoPreview
            cardType={card.type}
            filteredShape={getFilteredShape(
              searchFilters,
              card.colors?.[0]?.default_variation_shape
            )}
            orientation={orientation}
            isDigital={isDigital}
            template={photoPreviewTemplate}
            altText={altText}
            defaultImage={activeColorInfo.activeThumbnail} // Used for shapes preview display when no custom photo is uploaded
            sourceSizes={cardImageSizes}
            defaultWidth={defaultImageWidth}
            Fallback={() => (
              <div
                className={cx(styles.imagePlaceholder, {
                  [styles.selectedSample]:
                    allowMultiSample && multiSampleState.enabled && isMultiSampleSelection,
                })}
              >
                <LazyCardImage
                  className={styles.imageContent}
                  src={activeColorInfo.activeThumbnail}
                  alt={altText}
                  bypassLazy={bypassLazy}
                  sourceSizes={cardImageSizes}
                  defaultWidth={defaultImageWidth}
                  disableBorderRadius
                />
              </div>
            )}
            bypassLazy={bypassLazy}
          />
          <Tag cardType={card.type} isMobile={isMobile} />
          {!multiSampleState.enabled && (
            <FavoriteSuiteToggle
              className={styles.favoriteToggle}
              height={20}
              suiteType={card.type}
              suiteUUID={card.suiteUUID}
              suiteKey={card.key}
              activeColor={activeColorInfo.activeColor}
            />
          )}
        </div>
        <div>
          {showProductType && <ProductType>{getSingularTitleByCardType(card.type)}</ProductType>}
          <div data-testid="productName" className={styles.productNameSection}>
            <span className={cx(styles.productName, 'product-name')}>
              {card.suiteName && card.suiteName.split(' - ')[1]}
            </span>
            {isThemeMatchedCard && !isDigital && (
              <MatchThemeTag className={styles.tag} greenVersion />
            )}
            {card.foil && !card.letterpress && <FoilTag className={styles.tag} />}
            {card.letterpress && !card.foil && <LetterpressTag className={styles.tag} />}
            {card.collaborator?.toLowerCase() === 'disney' && <MagicTag className={styles.tag} />}
            {collaborator && (
              <TagV2 className={styles.tag} variant="purple">
                Zola x {collaborator.label}
              </TagV2>
            )}
            {card.newSuite && (
              <TagV2 className={styles.tag} variant="cove">
                New
              </TagV2>
            )}
          </div>
          {isDigital && <div className={styles.digitalFree}>Free</div>}
        </div>
      </Link>

      {!lightView && (
        <ProductColorPicker
          colors={card.colors}
          activeColor={activeColorInfo.activeColor}
          maxColorsToShow={maxColors}
          onSelect={colorObject => handleSelectColor(colorObject)}
          onMoreClick={e => handleClick(e, pathname)}
          swatchWidth={isMobile ? 20 : 16}
          isScrollable={isMobile}
        />
      )}

      <div className={styles.tileBottomRow}>
        {!multiSampleStateActive && !isDigital && (
          <PriceElement isDigital={isDigital} card={card} promotionData={promotionData} />
        )}

        <div className={styles.tileButtonRow}>
          {promotionData && !isDigital && (
            <ProductPrice
              price={card.min_price_cents / 100}
              isFree={isDigital || !card.min_price_cents}
              promotionData={promotionData}
              isOneLine
            />
          )}
          <div className={styles.tileButtonRowInnerWrapper}>
            <React.Fragment>
              {card.singleSampleAvailable && !isMobile && !multiSampleStateActive && (
                <ButtonV3
                  className={styles.hoverOptionsCTA}
                  variant="secondary"
                  onClick={enableSamples}
                >
                  Get sample
                </ButtonV3>
              )}
              {!multiSampleStateActive && !allowQuickAddToCart && (
                <ButtonV3 variant="primary" onClick={e => handleClick(e, pathname)}>
                  {isPaperAddOns ? 'Select' : 'Customize'}
                </ButtonV3>
              )}
              {allowQuickAddToCart && (
                <QuickAddToCartCTA activeColor={activeColorInfo.activeColor} card={card} />
              )}
            </React.Fragment>
          </div>
        </div>
      </div>

      {multiSampleStateActive && (
        <CheckBoxElement
          className={styles.multiSampleCheckbox}
          checkboxDisabled={checkboxDisabled}
          isMultiSampleSelection={isMultiSampleSelection}
          card={card}
          activeColor={activeColorInfo.activeColor}
          setMultisampleSelected={setMultisampleSelected}
        />
      )}
    </div>
  );
};

ListingDetailBase.propTypes = {
  card: PropTypes.shape({
    colors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    foil: PropTypes.bool,
    min_price_cents: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    newSuite: PropTypes.bool,
    suiteUUID: PropTypes.string.isRequired,
    type: PropTypes.string,
    uuid: PropTypes.string,
  }).isRequired,
  collaborator: PropTypes.shape({}),
  filterColor: PropTypes.string,
  filterMultiColor: PropTypes.arrayOf(PropTypes.string),
  isAdminView: PropTypes.bool,
  initialVariationColor: PropTypes.string,
  lightView: PropTypes.bool,
  plpOnClickCallback: PropTypes.func,
  allowMultiSample: PropTypes.bool,
  multiSampleState: PropTypes.shape({
    selected: PropTypes.shape({}),
    enabled: PropTypes.bool,
  }),
  setMultisampleSelected: PropTypes.func,
  setMultiSampleEnabled: PropTypes.func,
  bypassLazy: PropTypes.bool,
  promotionData: PropTypes.shape({}),
  allowQuickAddToCart: PropTypes.bool,
  cardImageSizes: PropTypes.arrayOf(PropTypes.shape({})),
  defaultImageWidth: PropTypes.number,
};

ListingDetailBase.defaultProps = {
  allowMultiSample: false,
  bypassLazy: false,
  multiSampleState: {
    selected: {},
    enabled: false,
  },
  setMultisampleSelected: () => {},
  setMultiSampleEnabled: () => {},
};

export const mapStateToProps = state => {
  return {
    multiSampleState: getMultiSampleState(state),
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    setMultisampleSelected: (card, selected, initialColor) =>
      dispatch(setMultiSampleSelectedAction(card, selected, initialColor)),
    setMultiSampleEnabled: (cardType, enabled) =>
      dispatch(setMultiSampleEnabledAction(cardType, enabled)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ListingDetailBase);
